import { createReducer, createStandardAction } from "typesafe-actions";
import { useSelector } from "react-redux";
import { State } from "./index";

/**
 * These are set in .gitlab-ci.yml for our deployments
 * local === "Development"
 * deployed Dev === "dev"
 * deployed Stage === "stage"
 * deployed Prod === "prod"
 */
export type EnvNames = "Development" | "dev" | "stage" | "prod";

const validateEnv = (name: string, defaultValue: string = ""): string => {
  const value = process.env[name];
  if (value !== undefined) {
    return value;
  }
  return defaultValue;
};

const validateEnvBool = (name: string): boolean => {
  return validateEnv(name) === "true";
};

const validateEnvNames = (
  name: string,
  defaultValue: EnvNames = "Development"
): EnvNames => {
  const value = process.env[name];
  switch (value) {
    case "Development":
    case "dev":
    case "stage":
    case "prod":
      return value;
    default:
      return defaultValue;
  }
};

export type Env = {
  envName: EnvNames;
  version: string;
  googleAnalyticsId: string;
  graphqlEndpoint: string;
  authOClientId: string;
  auth0Domain: string;
  rootUrl: string;
  optimizelySdkKey: string;
  useMock: boolean;
  mocksSimulateAuth: boolean;
  dataDogToken?: string;
  dataDogAppId?: string;
  isDataDogUser: boolean;
  verboseUrqlLogs: boolean;
  verboseAnalyticsLogs: boolean;
  verboseLocalLogs: boolean;
  debugUserRoles: boolean;
  microsoftClientId: string;
  microsoftClientSecret: string;
};
declare global {
  //https://docs.datadoghq.com/synthetics/identify_synthetics_bots/?tab=browsertests
  // Use to id any synthentic tests, mostly you just might want to use this to ignore
  // that user for things like Google Analytics or RUM
  interface Window {
    _DATADOG_SYNTHETICS_BROWSER: any;
  }
}

const EnvInit: Env = {
  envName: "Development",
  version: "0",
  googleAnalyticsId: "",
  graphqlEndpoint: "",
  auth0Domain: "",
  authOClientId: "",
  rootUrl: "",
  optimizelySdkKey: "",
  useMock: false,
  mocksSimulateAuth: false,
  isDataDogUser: window._DATADOG_SYNTHETICS_BROWSER !== undefined,
  verboseAnalyticsLogs: false,
  verboseUrqlLogs: false,
  verboseLocalLogs: false,
  debugUserRoles: false,
  microsoftClientId: "",
  microsoftClientSecret: ""
};
const AllVerboseLogs = validateEnvBool("REACT_APP_VERBOSE_ALL");
/**
 * Exported to help with initializing things outside the react tree.
 * React Components should favor either useSelector((state: State) => state.env.youREnvVariable) or useEnv hook.
 */
export const DefaultEnv: Env = {
  ...EnvInit,
  envName: validateEnvNames("REACT_APP_ENV_NAME", "Development"),
  version: validateEnv("REACT_APP_ENV_VERSION", "0"),
  googleAnalyticsId: validateEnv("REACT_APP_GOOGLE_ANALYTICS_ID"),
  graphqlEndpoint: validateEnv("REACT_APP_GRAPHQL_ENDPOINT"),
  auth0Domain: validateEnv("REACT_APP_AUTH0_DOMAIN"),
  authOClientId: validateEnv("REACT_APP_AUTH0_CLIENT_ID"),
  rootUrl: validateEnv("REACT_APP_ROOT_URL"),
  optimizelySdkKey: validateEnv("REACT_APP_OPTIMIZELY_SDK_KEY"),
  useMock: validateEnvBool("REACT_APP_GRAPHQL_USE_MOCK"),
  mocksSimulateAuth: validateEnvBool("REACT_APP_GRAPHQL_MOCKS_SIMULATE_AUTH"),
  dataDogAppId: validateEnv("REACT_APP_DATA_DOG_APP_ID"),
  dataDogToken: validateEnv("REACT_APP_DATA_DOG_TOKEN"),
  verboseAnalyticsLogs:
    AllVerboseLogs || validateEnvBool("REACT_APP_VERBOSE_ANALYTICS_LOGS"),
  verboseUrqlLogs:
    AllVerboseLogs || validateEnvBool("REACT_APP_VERBOSE_URQL_LOGS"),
  verboseLocalLogs:
    AllVerboseLogs || validateEnvBool("REACT_APP_VERBOSE_LOCAL_LOGS"),
  microsoftClientId: validateEnv("REACT_APP_MICROSOFT_CLIENT_ID")
};
export const useEnv = () => useSelector((state: State) => state.env);

export const updateEnv = createStandardAction("UPDATE_ENV")<Partial<Env>>();
export const EnvReducer = createReducer(DefaultEnv).handleAction(
  updateEnv,
  (state, { payload }) => {
    return {
      ...state,
      ...payload
    };
  }
);
